import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  ArrowLeft,
  Mail,
  Phone,
  Edit,
  Calendar,
  Percent,
  CreditCard,
  Plus,
  Trash2,
  FilePlus,
  Activity,
  Store,
  Link,
  FileText,
  Unlink,
  Check,
  X,
  Download,
  Upload,
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/ui/data-table";
import { CountrySelect } from "@/components/ui/country-select";
import { LoaderButton } from "@/components/ui/loader-button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DelinkConfirmationDialog } from "@/components/program/DelinkConfirmationDialog";
import { APIClient, ClientApi, ProgramApi } from "@/api";
// Import permission components and constants
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES, FEATURE_CODES } from "@/constants/permissions";
import {
  getDaysByFrequency,
  getFileUrl,
  getFrequencyByDays,
  isValidDate,
} from "@/utils";
import * as Yup from "yup";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  PAYMENT_CURRENCY_OPTIONS,
  PROGRAM_TYPE_OPTIONS,
} from "@/constants/program.constant";
import countries from "@/constants/array/country_city.json";
import { DocumentApi, DocumentUploadResponse } from "@/api/document.api";
import { TransferApi, ProgramAssignmentDataResponse } from "@/api/transfer.api";
import { EditTransferPartnerSheet } from "@/components/xpandpoint/EditTransferPartnerSheet";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { ActivityBlockchainDetails } from "@/components/common/ActivityBlockchainDetails";
import { BASE_URL } from "@/config";

// Contact form schema for Transfer Partner contacts (mirrors Program Details)
const contactSchema = Yup.object().shape({
  name: Yup.string()
    .required("Contact name is required")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  email: Yup.string()
    .required("Email is required")
    .email("Email must be a valid email address"),
  role: Yup.string()
    .required("Role is required")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  phoneCountry: Yup.string().required("Country code is required"),
  phoneNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^\d+$/, "Phone must contain digits only")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number cannot exceed 15 digits"),
});

const documentUploadSchema = Yup.object().shape({
  documentName: Yup.string().required("Document name is required"),
  file: Yup.mixed().required("File is required"),
});

const formatRelativeTime = (date: Date) => {
  const relativeTime = formatDistanceToNow(date, { addSuffix: true });
  // Replace "about 1 year ago" with "more than a year ago"
  return relativeTime.replace(/about 1 year ago/g, "more than a year ago");
};

const TransferPartnerDetails = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const routeTransferPartner = (
    location.state as { transferPartner?: any } | null
  )?.transferPartner;
  const [transferPartner, setTransferPartner] = useState(
    routeTransferPartner ?? null
  );
  const [isLoading, setIsLoading] = useState(!routeTransferPartner);
  const [loadError, setLoadError] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [isDeleteContactDialogOpen, setIsDeleteContactDialogOpen] =
    useState(false);
  const [contactToDelete, setContactToDelete] = useState<any | null>(null);
  const [deletingContact, setDeletingContact] = useState(false);

  const contactForm = useForm<any>({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "",
      phoneCountry: "+971",
      phoneNumber: "",
    },
  });

  // Programs tab state
  const [programs, setPrograms] = useState<any[]>([
    {
      id: "1",
      programName: "Loyalty Plus",
      programCode: "LP-001",
      status: "Active",
      type: "Points",
      category: "Retail",
      logo: "/placeholder.svg",
      address: "123 Main St, Cityville",
      country: "USA",
      city: "Cityville",
      client: "Acme Corp",
      isActive: true,
      transferPartnerProgramIdForSelectedTransferPartner: null,
    },
    {
      id: "2",
      programName: "Rewards Club",
      programCode: "RC-002",
      status: "Inactive",
      type: "Cashback",
      category: "Travel",
      logo: "/placeholder.svg",
      address: "456 Side Rd, Townsville",
      country: "Canada",
      city: "Townsville",
      client: "Beta Inc",
      isActive: false,
      transferPartnerProgramIdForSelectedTransferPartner: null,
    },
  ]);
  const [viewType, setViewType] = useState<"card" | "table">("card");
  const [isAddProgramDialogOpen, setIsAddProgramDialogOpen] = useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLinkConfirmationOpen, setIsLinkConfirmationOpen] = useState(false);
  const [isDelinkConfirmationOpen, setIsDelinkConfirmationOpen] =
    useState(false);
  const [programToDelink, setProgramToDelink] = useState<string>("");
  const [activeTab, setActiveTab] = useState("details");
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityPage, setActivityPage] = useState(0);
  const [activityPageSize, setActivityPageSize] = useState(10);
  const [activityTotalRecords, setActivityTotalRecords] = useState(0);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programsError, setProgramsError] = useState<string | null>(null);
  const [unassignedPrograms, setUnassignedPrograms] = useState<any[]>([]);

  // Table columns for programs
  const programColumns = [
    {
      header: "Program",
      cell: (row: any) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
            <img
              src={row.logo ? `${BASE_URL}${row.logo}` : "/placeholder.svg"}
              alt={row.programName}
              className="max-h-8 max-w-8"
            />
          </div>
          <div>
            <h4
              className="text-lg font-semibold text-gray-800"
              title={row.programName}
            >
              {row.programName}
            </h4>
            <div className="text-xs text-gray-600 mb-1">
              Code: {row.programCode}
            </div>
            <p className="text-xs text-gray-500">{row.type} Program</p>
          </div>
        </div>
      ),
    },
    {
      header: "Client",
      cell: (row: any) => <p className="text-gray-800">{row.client}</p>,
    },
    {
      header: "Address",
      cell: (row: any) => (
        <p className="text-gray-800 truncate max-w-[200px]">{row.address}</p>
      ),
    },
    {
      header: "Status",
      cell: (row: any) => (
        <div
          className={`inline-flex px-2 py-1 rounded-full text-xs ${
            row.isActive
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {row.isActive ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      header: "Actions",
      cell: (row: any) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-500 hover:text-red-600"
          onClick={() => {
            setProgramToDelink(row.id);
            setIsDelinkConfirmationOpen(true);
          }}
          disabled={delinkingProgram}
        >
          <Unlink className="h-4 w-4 mr-1" /> Delink
        </Button>
      ),
    },
  ];

  // Delink handler with API call
  const [delinkingProgram, setDelinkingProgram] = useState(false);

  const handleDelinkProgram = async (id: string) => {
    if (!transferPartner?.id) return;

    const programToDelink = programs.find((p) => p.id === id);
    if (!programToDelink?.transferPartnerProgramIdForSelectedTransferPartner) {
      toast.error("Partner ID not found for delinking");
      return;
    }

    setDelinkingProgram(true);
    try {
      // Call API to unassign program
      await TransferApi.unAssignProgramsFromTransferPartner([
        programToDelink.transferPartnerProgramIdForSelectedTransferPartner,
      ]);

      // Refresh programs list
      const { data } = await TransferApi.getProgramAssignmentDataFromDB({
        transferPartnerId: transferPartner.id,
        programType: "TRANSFER",
      });

      // Update assigned programs
      setPrograms(
        Array.isArray(data?.assignedPrograms)
          ? data.assignedPrograms.map((p: any) => ({
              id: p.id?.toString() ?? p.id,
              programName: p.programName,
              programCode: p.programCode,
              status: p.status || (p.isActive ? "Active" : "Inactive"),
              type: p.programType || "-",
              category: p.category || "-",
              logo: p.programLogoUri || "/placeholder.svg",
              address: p.address || "-",
              country: p.country || "-",
              city: p.city || "-",
              client: p.clientVO?.clientName || "-",
              isActive: p.isActive,
              transferPartnerProgramIdForSelectedTransferPartner:
                p.transferPartnerProgramIdForSelectedTransferPartner,
            }))
          : []
      );

      // Update unassigned programs
      setUnassignedPrograms(
        Array.isArray(data?.unAssignedPrograms)
          ? data.unAssignedPrograms.map((p: any) => ({
              id: p.id?.toString() ?? p.id,
              programName: p.programName,
              programCode: p.programCode,
              type: p.programType || "-",
              logo: p.programLogoUri || "/placeholder.svg",
              currentFromCppForSelectedTransferPartner:
                p.currentFromCppForSelectedTransferPartner,
              currentToCppForSelectedTransferPartner:
                p.currentToCppForSelectedTransferPartner,
              costPerPoint: p.costPerPoint,
            }))
          : []
      );

      setIsDelinkConfirmationOpen(false);
      toast.success("Partner successfully delinked");
    } catch (error: any) {
      console.error("Error delinking partner:", error);
      toast.error(error?.message || "Failed to delink partner");
    } finally {
      setDelinkingProgram(false);
    }
  };

  // Link handler with API call
  const [linkingPrograms, setLinkingPrograms] = useState(false);

  const handleLinkPrograms = async () => {
    if (!transferPartner?.id || selectedPrograms.length === 0) return;

    setLinkingPrograms(true);
    try {
      // Prepare payload for API
      const assignments = selectedPrograms.map((programId) => {
        const program = unassignedPrograms.find((p) => p.id === programId);
        return {
          isActive: true,
          transferPartnerId: transferPartner.id,
          programId: parseInt(programId),
          transferPartnerProgramCppVO: {
            fromCpp:
              program?.currentFromCppForSelectedTransferPartner ||
              program?.costPerPoint ||
              0,
            toCpp:
              program?.currentToCppForSelectedTransferPartner ||
              program?.costPerPoint ||
              0,
            startDate: new Date().getTime(),
            endDate: new Date().getTime(),
          },
        };
      });

      // Call API to assign programs
      await TransferApi.assignProgramsToTransferPartner(assignments);

      // Refresh programs list
      const { data } = await TransferApi.getProgramAssignmentDataFromDB({
        transferPartnerId: transferPartner.id,
        programType: "TRANSFER",
      });

      // Update assigned programs
      setPrograms(
        Array.isArray(data?.assignedPrograms)
          ? data.assignedPrograms.map((p: any) => ({
              id: p.id?.toString() ?? p.id,
              programName: p.programName,
              programCode: p.programCode,
              status: p.status || (p.isActive ? "Active" : "Inactive"),
              type: p.programType || "-",
              category: p.category || "-",
              logo: p.programLogoUri || "/placeholder.svg",
              address: p.address || "-",
              country: p.country || "-",
              city: p.city || "-",
              client: p.clientVO?.clientName || "-",
              isActive: p.isActive,
              transferPartnerProgramIdForSelectedTransferPartner:
                p.transferPartnerProgramIdForSelectedTransferPartner,
            }))
          : []
      );

      // Update unassigned programs
      setUnassignedPrograms(
        Array.isArray(data?.unAssignedPrograms)
          ? data.unAssignedPrograms.map((p: any) => ({
              id: p.id?.toString() ?? p.id,
              programName: p.programName,
              programCode: p.programCode,
              type: p.programType || "-",
              logo: p.programLogoUri || "/placeholder.svg",
            }))
          : []
      );

      // Close modal and reset
      setIsAddProgramDialogOpen(false);
      setSelectedPrograms([]);
      setSearchQuery("");
      setIsLinkConfirmationOpen(false);

      toast.success(
        `Successfully linked ${selectedPrograms.length} partners(s)`
      );
    } catch (error: any) {
      console.error("Error linking programs:", error);
      toast.error(error?.message || "Failed to link partner");
    } finally {
      setLinkingPrograms(false);
    }
  };

  useEffect(() => {
    if (location.state?.transferPartner) {
      setTransferPartner(location.state.transferPartner);
    }
  }, [location.state?.transferPartner]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    async function fetchTransferPartnerById() {
      if (!routeTransferPartner) {
        setIsLoading(true);
      }
      setLoadError(false);
      try {
        const { data } = await TransferApi.getTransferPartnerUsingId(id);
        const partner =
          data?.transferPartnerVO ||
          (Array.isArray(data?.transferPartnerVOList)
            ? data.transferPartnerVOList[0]
            : null) ||
          (data?.id != null ? data : null);

        if (cancelled) return;

        if (!partner) {
          if (!routeTransferPartner) {
            setLoadError(true);
            setTransferPartner(null);
          }
          toast.error("Program not found");
          return;
        }

        setTransferPartner(partner);
      } catch {
        if (!cancelled) {
          if (!routeTransferPartner) {
            setLoadError(true);
            setTransferPartner(null);
          }
          toast.error("Failed to fetch program details");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchTransferPartnerById();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const fetchContacts = async (partnerId: number | string) => {
    try {
      const res = await APIClient(
        `/transferPartner/getTransferPartnerContacts?transferPartnerId=${partnerId}`
      );
      setContacts(res.data || []);
    } catch {
      setContacts([]);
    }
  };

  const handlePartnerUpdated = (updatedPartner: any) => {
    setTransferPartner(updatedPartner);
    navigate(`/xpandpoint/transfer-programs/${id}`, {
      replace: true,
      state: { transferPartner: updatedPartner },
    });
    if (updatedPartner?.id) {
      fetchContacts(updatedPartner.id);
    }
  };

  useEffect(() => {
    if (transferPartner?.id) {
      fetchContacts(transferPartner.id);
    }
  }, [transferPartner?.id]);

  // Fetch programs when Programs tab is selected
  useEffect(() => {
    const fetchPrograms = async () => {
      if (activeTab === "programs" && transferPartner?.id) {
        setProgramsLoading(true);
        setProgramsError(null);
        try {
          const { data } = await TransferApi.getProgramAssignmentDataFromDB({
            transferPartnerId: transferPartner.id,
            programType: "TRANSFER",
          });
          setPrograms(
            Array.isArray(data?.assignedPrograms)
              ? data.assignedPrograms.map((p: any) => ({
                  id: p.id?.toString() ?? p.id,
                  programName: p.programName,
                  programCode: p.programCode,
                  status: p.status || (p.isActive ? "Active" : "Inactive"),
                  type: p.programType || "-",
                  category: p.category || "-",
                  logo: p.programLogoUri || "/placeholder.svg",
                  address: p.address || "-",
                  country: p.country || "-",
                  city: p.city || "-",
                  client: p.clientVO?.clientName || "-",
                  isActive: p.isActive,
                  transferPartnerProgramIdForSelectedTransferPartner:
                    p.transferPartnerProgramIdForSelectedTransferPartner,
                }))
              : []
          );
          setUnassignedPrograms(
            Array.isArray(data?.unAssignedPrograms)
              ? data.unAssignedPrograms.map((p: any) => ({
                  id: p.id?.toString() ?? p.id,
                  programName: p.programName,
                  programCode: p.programCode,
                  type: p.programType || "-",
                  logo: p.programLogoUri || "/placeholder.svg",
                  currentFromCppForSelectedTransferPartner:
                    p.currentFromCppForSelectedTransferPartner,
                  currentToCppForSelectedTransferPartner:
                    p.currentToCppForSelectedTransferPartner,
                  costPerPoint: p.costPerPoint,
                }))
              : []
          );
        } catch (err: any) {
          setProgramsError(err?.message || "Failed to fetch programs");
          setUnassignedPrograms([]);
        } finally {
          setProgramsLoading(false);
        }
      }
    };
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, transferPartner?.id]);

  const fetchActivityLogs = async () => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const partnerCode = transferPartner?.partnerCode;
      if (!partnerCode) {
        setActivityLogs([]);
        setActivityTotalRecords(0);
        setActivityLoading(false);
        return;
      }
      const payload = {
        id: null,
        partnerCode,
        programCode: null,
        merchantCode: null,
        pageNumber: activityPage,
        pageSize: activityPageSize,
        sortOrder: "desc",
        sortBy: "createdAt",
      };
      const { data } = await ProgramApi.getActivityLogs(payload);
      setActivityLogs(data.activityLogVOs || []);
      setActivityTotalRecords(data.totalRecords || 0);
    } catch (err) {
      setActivityError("Failed to fetch activity logs");
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "activity" && transferPartner?.partnerCode) {
      fetchActivityLogs();
    }
  }, [
    activeTab,
    transferPartner?.partnerCode,
    activityPage,
    activityPageSize,
  ]);

  // Filter unassigned programs for the link dialog
  const filteredAvailablePrograms = unassignedPrograms.filter(
    (p) =>
      p.programName.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !selectedPrograms.includes(p.id)
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <RouterLink to="/xpandpoint/transfer-programs">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </RouterLink>
          </Button>
        </div>
        <Card className="w-full border-0 shadow-sm card-modern">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadError || !transferPartner) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <RouterLink to="/xpandpoint/transfer-programs">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </RouterLink>
          </Button>
        </div>
        <Card className="w-full border-0 shadow-sm card-modern">
          <CardContent className="p-6">
            <p className="text-center text-gray-500">
              Program not found.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with back button and main heading */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <RouterLink to="/xpandpoint/transfer-programs">
            {/* <RouterLink to="/xpandpoint/transfer-partners"> */}
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </RouterLink>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Program Details</h1>
          <p className="text-gray-500">View and manage program information</p>
        </div>
      </div>

      {/* Transfer Partner Header Section */}
      <div className="bg-white rounded-xl shadow-sm flex items-center justify-between p-6 mb-4">
        <div className="flex items-center gap-6">
          <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
            <img
              src={
                transferPartner?.partnerLogoUri
                  ? getFileUrl(transferPartner?.partnerLogoUri)
                  : "/placeholder.svg"
              }
              alt={transferPartner?.partnerName}
              className="max-h-16 max-w-16"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {transferPartner?.partnerName}
            </h2>
            <div className="flex items-center gap-3 mt-1 text-gray-500">
              <span className="text-sm">
                Code: {transferPartner?.partnerCode}
              </span>
              {transferPartner?.createdDateInMillis && (
                <span className="text-sm flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" /> Created around{" "}
                  {formatRelativeTime(
                    new Date(transferPartner?.createdDateInMillis)
                  )}
                </span>
              )}
              {transferPartner?.lastUpdatedDateInMillis && (
                <span className="text-sm flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" /> Updated{" "}
                  {formatRelativeTime(
                    new Date(transferPartner?.lastUpdatedDateInMillis)
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        <PermissionGuard
          permissionCode={PERMISSION_CODES.UPDATE_TRANSFER_PARTNER}
        >
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-base font-medium flex items-center gap-2"
            onClick={() => setEditSheetOpen(true)}
          >
            <Edit className="h-4 w-4 mr-1" /> Edit Program
          </Button>
        </PermissionGuard>
      </div>

      {/* Tabs for details and contacts */}
      <Tabs
        defaultValue="details"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger
            value="details"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Program Details
          </TabsTrigger>
          <TabsTrigger
            value="contacts"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Contacts
          </TabsTrigger>
          <TabsTrigger
            value="programs"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Partners
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Name</p>
                    <p className="text-gray-800">
                      {transferPartner.partnerName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Code</p>
                    <p className="text-gray-800">
                      {transferPartner.partnerCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Description
                    </p>
                    <p className="text-gray-800">
                      {transferPartner.businessDescription}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Client</p>
                    <p className="text-gray-800">
                      {transferPartner.clientVO?.clientName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      VAT Number
                    </p>
                    <p className="text-gray-800">{transferPartner.taxId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Website URL
                    </p>
                    <p className="text-gray-800">
                      {transferPartner.websiteUrl ? (
                        <a
                          href={transferPartner.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          {transferPartner.websiteUrl}
                        </a>
                      ) : (
                        "-"
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Loyalty Details */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Loyalty Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Loyalty Currency
                    </p>
                    <p className="text-gray-800 font-medium">
                      {transferPartner.loyaltyCurrency}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Redemption CPP
                    </p>
                    <p className="text-gray-800 font-medium">
                      {transferPartner.redemptionCpp ||
                        transferPartner.costPerPoint}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Payment Currency
                    </p>
                    <p className="text-gray-800 font-medium">
                      {transferPartner.currencyVO?.iso3CurrencyCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Points Increment
                    </p>
                    <p className="text-gray-800 font-medium">
                      {transferPartner.pointsIncrement}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Location Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Address</p>
                    <p className="text-gray-800">
                      {transferPartner.businessLocation}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Country</p>
                    <p className="text-gray-800">{transferPartner.country}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">City</p>
                    <p className="text-gray-800">{transferPartner.city}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Program Contacts
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => setIsAddContactDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" /> Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {Array.isArray(contacts) && contacts.length > 0 ? (
                <div className="space-y-6">
                  {contacts.map((contact, index) => (
                    <div
                      key={contact?.id || index}
                      className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                    >
                      <div className="mb-2 sm:mb-0">
                        <p className="font-medium text-gray-800">
                          {contact?.contactName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {contact?.contactRole}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center text-gray-500">
                          <Mail className="h-4 w-4 mr-1" />
                          <span className="text-sm">
                            {contact?.contactEmail}
                          </span>
                        </div>
                        {contact?.phoneCountryCode && contact?.phoneNumber && (
                          <div className="flex items-center text-gray-500">
                            <Phone className="h-4 w-4 mr-1" />
                            <span className="text-sm">
                              <span className="text-gray-400">
                                {contact?.phoneCountryCode}{" "}
                              </span>
                              {contact?.phoneNumber}
                            </span>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-red-600 p-1 h-auto"
                          onClick={() => {
                            setContactToDelete(contact);
                            setIsDeleteContactDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No contacts"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">No Contacts Found</p>
                  <p className="mb-4">
                    No contacts are currently added to this transfer partner.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={() => setIsAddContactDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" /> Create Your First Contact
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add Contact Dialog */}
          <Dialog
            open={isAddContactDialogOpen}
            onOpenChange={(open) => {
              setIsAddContactDialogOpen(open);
              if (!open) contactForm.reset();
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Contact</DialogTitle>
              </DialogHeader>
              <Form {...contactForm}>
                <form
                  onSubmit={contactForm.handleSubmit(async (values) => {
                    if (!transferPartner?.id) return;
                    setAddingContact(true);
                    try {
                      const payload = {
                        contactName: values.name,
                        contactEmail: values.email,
                        contactRole: values.role,
                        phoneNumber: values.phoneNumber,
                        phoneCountryCode: values.phoneCountry,
                        partnerId: transferPartner.id,
                      };
                      await APIClient(
                        "/transferPartner/createOrUpdateTransferPartnerContact",
                        payload,
                        { requestType: "post" }
                      );
                      // refresh contacts
                      const res = await APIClient(
                        `/transferPartner/getTransferPartnerContacts?transferPartnerId=${transferPartner.id}`
                      );
                      setContacts(res.data || []);
                      toast.success("Contact added successfully");
                      setIsAddContactDialogOpen(false);
                      contactForm.reset();
                    } catch (err: any) {
                      toast.error(err?.message || "Failed to add contact");
                    } finally {
                      setAddingContact(false);
                    }
                  })}
                  className="space-y-4"
                >
                  <FormField
                    control={contactForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter contact name"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^A-Za-z ]/g,
                                ""
                              );
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={contactForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter contact email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={contactForm.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter contact role"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^A-Za-z ]/g,
                                ""
                              );
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-[110px_1fr] gap-2">
                    <FormField
                      control={contactForm.control}
                      name="phoneCountry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <CountrySelect
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={contactForm.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="opacity-0">Number</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter phone number"
                              {...field}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              onChange={(e) => {
                                const digits = e.target.value.replace(
                                  /\D/g,
                                  ""
                                );
                                field.onChange(digits);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <DialogFooter className="sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsAddContactDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <LoaderButton type="submit" loading={addingContact}>
                      <Plus className="h-4 w-4 mr-2" /> Add Contact
                    </LoaderButton>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>

          {/* Delete Contact Confirmation */}
          <Dialog
            open={isDeleteContactDialogOpen}
            onOpenChange={setIsDeleteContactDialogOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Delete Contact</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this contact? This action
                  cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDeleteContactDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!contactToDelete) return;
                    const contactId =
                      contactToDelete?.id ||
                      contactToDelete?.transferPartnerContactId;
                    if (!contactId) {
                      toast.error("Contact id not found");
                      return;
                    }
                    setDeletingContact(true);
                    try {
                      await APIClient(
                        `/transferPartner/deleteTransferPartnerContact?transferPartnerContactId=${contactId}`
                      );
                      if (transferPartner?.id) {
                        const res = await APIClient(
                          `/transferPartner/getTransferPartnerContacts?transferPartnerId=${transferPartner.id}`
                        );
                        setContacts(res.data || []);
                      }
                      toast.success("Contact deleted");
                      setIsDeleteContactDialogOpen(false);
                      setContactToDelete(null);
                    } catch (err: any) {
                      toast.error(err?.message || "Failed to delete contact");
                    } finally {
                      setDeletingContact(false);
                    }
                  }}
                  disabled={deletingContact}
                >
                  {deletingContact ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Programs Tab */}
        <TabsContent value="programs" className="pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Linked Partners
              </CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Table View</span>
                  <Switch
                    checked={viewType === "card"}
                    onCheckedChange={(checked) =>
                      setViewType(checked ? "card" : "table")
                    }
                  />
                  <span className="text-sm text-gray-600">Card View</span>
                </div>
                <Button
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => setIsAddProgramDialogOpen(true)}
                  disabled={linkingPrograms}
                >
                  <Link className="h-4 w-4" /> Link Partner
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {programsLoading ? (
                <div className="text-center py-8 text-gray-500">
                  Loading partners...
                </div>
              ) : programsError ? (
                <div className="text-center py-8 text-red-500">
                  {programsError}
                </div>
              ) : programs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No programs"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">No Partners Found</p>
                  <p className="mb-4">
                    No partner are currently linked to this program.
                  </p>
                  {/* <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={() => setIsAddProgramDialogOpen(true)}
                    disabled={linkingPrograms}
                  >
                    <Link className="h-4 w-4" /> Link Your First Partner
                  </Button> */}
                </div>
              ) : viewType === "card" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {programs.map((program) => (
                    <Card
                      key={program.id}
                      className="border-0 shadow-md rounded-xl bg-gradient-to-br from-white to-gray-50 hover:shadow-lg transition-shadow"
                    >
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center shadow-sm border">
                            <img
                              // src={"/placeholder.svg"}
                              src={
                                program.logo
                                  ? `${BASE_URL}${program.logo}`
                                  : "/placeholder.svg"
                              }
                              alt={program.programName}
                              className="max-h-14 max-w-14 object-contain"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4
                              className="text-xl font-bold text-gray-900 mb-1"
                              title={program.programName}
                            >
                              {program.programName}
                            </h4>
                            <div className="text-xs text-gray-500 mb-1">
                              Code:{" "}
                              <span className="font-semibold">
                                {program.programCode}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 mb-1">
                              <Badge
                                variant="secondary"
                                className="bg-blue-50 text-blue-700 border-blue-100 px-2 py-0.5 text-xs font-medium"
                              >
                                {program.category}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2 items-center mt-1">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  program.isActive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {program.isActive ? "Active" : "Inactive"}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 font-semibold">
                                {program.type} Partner
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 flex flex-col gap-2 text-sm mt-2">
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span>
                              {[program.address, program.city, program.country]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span>{program.client}</span>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 hover:text-red-600"
                            onClick={() => {
                              setProgramToDelink(program.id);
                              setIsDelinkConfirmationOpen(true);
                            }}
                            disabled={delinkingProgram}
                          >
                            <Unlink className="h-4 w-4 mr-1" /> Delink
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <DataTable title="" data={programs} columns={programColumns} />
              )}
            </CardContent>
          </Card>
          {/* Link Programs Dialog */}
          <Dialog
            open={isAddProgramDialogOpen}
            onOpenChange={setIsAddProgramDialogOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Link Partners</DialogTitle>
                <DialogDescription>
                  Select partner to link to this program.
                </DialogDescription>
              </DialogHeader>
              <div className="my-4">
                <div className="mb-2">
                  <p className="text-sm font-medium mb-1">Selected Partners:</p>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {selectedPrograms.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No partners selected
                      </p>
                    ) : (
                      selectedPrograms.map((programId) => {
                        const program = unassignedPrograms.find(
                          (p) => p.id === programId
                        );
                        return program ? (
                          <Badge
                            key={programId}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {program.programName}
                            <button
                              onClick={() =>
                                setSelectedPrograms(
                                  selectedPrograms.filter(
                                    (id) => id !== programId
                                  )
                                )
                              }
                              className="ml-1 h-3 w-3 rounded-full bg-gray-400 text-white flex items-center justify-center"
                            >
                              <X className="h-2 w-2" />
                            </button>
                          </Badge>
                        ) : null;
                      })
                    )}
                  </div>
                </div>
                <Command className="rounded-lg border shadow-md">
                  <CommandInput
                    placeholder="Search partners..."
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList className="max-h-40 overflow-auto">
                    <CommandEmpty>No partners found.</CommandEmpty>
                    <CommandGroup>
                      {filteredAvailablePrograms.length === 0 ? (
                        <div className="py-6 text-center text-sm">
                          No available partners to link
                        </div>
                      ) : (
                        filteredAvailablePrograms.map((program) => (
                          <CommandItem
                            key={program.id}
                            onSelect={() =>
                              setSelectedPrograms([
                                ...selectedPrograms,
                                program.id,
                              ])
                            }
                            className="flex items-center gap-2 py-3"
                          >
                            <div
                              className={`flex h-5 w-5 items-center justify-center rounded-md border ${
                                selectedPrograms.includes(program.id)
                                  ? "bg-primary border-primary"
                                  : "border-gray-300"
                              }`}
                            >
                              {selectedPrograms.includes(program.id) && (
                                <Check className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <div className="flex items-center gap-3 flex-1">
                              <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                                <img
                                  src={
                                    program.logo
                                      ? `${BASE_URL}${program.logo}`
                                      : "/placeholder.svg"
                                  }
                                  // src={
                                  //   program.programLogoUri &&
                                  //   program.logo !== "/placeholder.svg"
                                  //     ? program.logo.startsWith("http")
                                  //       ? program.logo
                                  //       : `${BASE_URL}${program.logo}`
                                  //     : "/placeholder.svg"
                                  // }
                                  alt={program.programName}
                                  className="max-h-6 max-w-6"
                                />
                              </div>
                              <div>
                                <p className="text-sm font-medium">
                                  {program.programName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {program.type} Partner
                                </p>
                              </div>
                            </div>
                          </CommandItem>
                        ))
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </div>
              <DialogFooter className="sm:justify-end mt-7">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAddProgramDialogOpen(false);
                    setSelectedPrograms([]);
                    setSearchQuery("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => setIsLinkConfirmationOpen(true)}
                  disabled={selectedPrograms.length === 0}
                >
                  Link {selectedPrograms.length}{" "}
                  {selectedPrograms.length === 1 ? "Partner" : "Partners"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Link Confirmation Dialog */}
          <Dialog
            open={isLinkConfirmationOpen}
            onOpenChange={setIsLinkConfirmationOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Confirm Partner Linking</DialogTitle>
                <DialogDescription>
                  Are you sure you want to link {selectedPrograms.length}{" "}
                  {selectedPrograms.length === 1 ? "partner" : "partners"} to
                  this program?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsLinkConfirmationOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleLinkPrograms} disabled={linkingPrograms}>
                  {linkingPrograms ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Linking...
                    </>
                  ) : (
                    `Link ${
                      selectedPrograms.length === 1 ? "Partner" : "Partners"
                    }`
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Delink Confirmation Dialog */}
          <Dialog
            open={isDelinkConfirmationOpen}
            onOpenChange={setIsDelinkConfirmationOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Confirm Partner Delinking</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delink this partner from the program?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDelinkConfirmationOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelinkProgram(programToDelink)}
                  disabled={delinkingProgram}
                >
                  {delinkingProgram ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Delinking...
                    </>
                  ) : (
                    "Delink"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {activityLoading ? (
                <div className="text-center text-gray-500">
                  Loading activity...
                </div>
              ) : activityError ? (
                <div className="text-center text-red-500">{activityError}</div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center text-gray-500">
                  No activity found.
                </div>
              ) : (
                <>
                  <div className="divide-y divide-gray-100">
                    {activityLogs.map((log) => (
                      <div key={log.id} className="py-4 first:pt-0 last:pb-0">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                            <Activity className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">
                                {log.action
                                  .replace(/_/g, " ")
                                  .toLowerCase()
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                              </span>
                            </div>
                            <div
                              className="text-gray-700 text-sm mb-1 truncate"
                              title={log.description}
                            >
                              {log.description}
                            </div>
                            <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500 mt-1">
                              <span
                                title={log.actorName}
                                className="font-medium text-gray-600"
                              >
                                {log.actorName}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span>
                                {log.createdAt
                                  ? formatRelativeTime(new Date(log.createdAt))
                                  : ""}
                              </span>
                            </div>
                          </div>
                          <ActivityBlockchainDetails
                            blockchainDataVO={log.blockchainDataVO}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {!activityLoading && activityTotalRecords > 0 && (
                    <div className="mt-4 flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          Rows per page:
                        </span>
                        <Select
                          value={activityPageSize.toString()}
                          onValueChange={(value) => {
                            setActivityPageSize(Number(value));
                            setActivityPage(0);
                          }}
                        >
                          <SelectTrigger className="w-[80px]">
                            <SelectValue
                              placeholder={activityPageSize.toString()}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5">5</SelectItem>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {Math.ceil(activityTotalRecords / activityPageSize) >
                        1 && (
                        <EnhancedPaginationControl
                          totalPages={Math.ceil(
                            activityTotalRecords / activityPageSize
                          )}
                          currentPage={activityPage}
                          onPageChange={(page) => setActivityPage(page)}
                        />
                      )}
                      <div className="w-[160px]" />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <EditTransferPartnerSheet
        open={editSheetOpen}
        onOpenChange={setEditSheetOpen}
        transferPartner={transferPartner}
        contacts={contacts}
        onSuccess={handlePartnerUpdated}
      />
    </div>
  );
};

export default TransferPartnerDetails;
